// src/providers/AlertProvider.tsx
'use client';

import React, { createContext, useContext, ReactNode, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Dialog from '@/ui/dialogs/Dialog';
import { scrollUtils } from '@/utils/scrollUtils';

export interface AlertOptions {
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  showCancel?: boolean;
}

interface AlertState extends AlertOptions {
  isOpen: boolean;
}

interface AlertContextType {
  alert: (options: AlertOptions) => void;
  success: (title: string, message: string) => void;
  error: (title: string, message: string) => void;
  warning: (title: string, message: string) => void;
  info: (title: string, message: string) => void;
  confirm: (options: AlertOptions) => Promise<boolean>;
  closeAlert: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useAlertContext = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlertContext must be used within AlertProvider');
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alertState, setAlertState] = useState<AlertState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    showCancel: false,
  });
  
  const [confirmResolver, setConfirmResolver] = useState<((value: boolean) => void) | null>(null);
  const scrollPositionRef = useRef<number>(0);

  // Handle body scroll when dialog opens/closes
  useEffect(() => {
    if (alertState.isOpen) {
      // Save current scroll position before preventing scroll
      scrollPositionRef.current = scrollUtils.saveScrollPosition('alert-dialog-scroll');
      scrollUtils.preventBodyScroll();
    } else {
      // Restore scroll when dialog closes
      scrollUtils.allowBodyScroll();
      // Restore saved scroll position after a short delay
      setTimeout(() => {
        scrollUtils.restoreScrollPosition('alert-dialog-scroll');
      }, 50);
    }

    // Cleanup on unmount
    return () => {
      if (!alertState.isOpen) {
        scrollUtils.allowBodyScroll();
      }
    };
  }, [alertState.isOpen]);

  const alert = useCallback((options: AlertOptions) => {
    setAlertState({
      isOpen: true,
      type: 'info',
      confirmText: 'OK',
      cancelText: 'Cancel',
      showCancel: false,
      ...options,
    });
  }, []);

  const success = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'success', confirmText: 'OK', showCancel: false });
  }, [alert]);

  const error = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'error', confirmText: 'OK', showCancel: false });
  }, [alert]);

  const warning = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'warning', confirmText: 'OK', showCancel: false });
  }, [alert]);

  const info = useCallback((title: string, message: string) => {
    alert({ title, message, type: 'info', confirmText: 'OK', showCancel: false });
  }, [alert]);

  const confirm = useCallback((options: AlertOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmResolver(() => resolve);
      setAlertState({
        isOpen: true,
        type: 'warning',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        showCancel: true,
        ...options,
      });
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
    if (confirmResolver) {
      confirmResolver(false);
      setConfirmResolver(null);
    }
  }, [confirmResolver]);

  const handleConfirm = useCallback(async () => {
    if (alertState.onConfirm) {
      await alertState.onConfirm();
    }
    if (confirmResolver) {
      confirmResolver(true);
      setConfirmResolver(null);
    }
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, [alertState, confirmResolver]);

  const handleCancel = useCallback(() => {
    alertState.onCancel?.();
    if (confirmResolver) {
      confirmResolver(false);
      setConfirmResolver(null);
    }
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, [alertState, confirmResolver]);

  const contextValue = useMemo(() => ({
    alert,
    success,
    error,
    warning,
    info,
    confirm,
    closeAlert,
  }), [alert, success, error, warning, info, confirm, closeAlert]);

  return (
    <AlertContext.Provider value={contextValue}>
      {children}
      <Dialog
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        title={alertState.title}
        type={alertState.type}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        showCancel={alertState.showCancel}
        showConfirm={!!alertState.confirmText}
      >
        <p className="text-gray-600 dark:text-gray-400">{alertState.message}</p>
      </Dialog>
    </AlertContext.Provider>
  );
};